<template>
  <div>
    <customModal
      :containermd="true"
      :footerShow="true"
      @closeModal="closePopup"
    >
      <template #title>
        <span>Sign Document - {{ documentTitle }}</span>
      </template>

      <template #subHeaderPart>
        <span class="mr-3 small text-muted">Version {{ version }}</span>
      </template>

      <template #body>
        <div
          class="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom"
        >
          <div>
            <span class="text-secondary small"
              >Document ID: <strong>{{ shortDocId }}</strong></span
            >
          </div>
          <div class="bg-light px-3 py-1 rounded border">
            <span class="text-muted small mr-1">Signing as:</span>
            <strong class="text-primary small">{{ recipientEmail }}</strong>
          </div>
        </div>

        <div class="field-list">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0 font-weight-bold">Your signature fields</h6>
            <span class="badge badge-info">{{ listHint }}</span>
          </div>

          <div class="field-list-body">
            <div v-if="loadingFields" class="text-center py-4 text-muted">
              <i class="fa fa-spinner fa-spin mr-2"></i> Loading signature
              fields…
            </div>

            <template v-else>
              <div
                v-for="f in fieldsForUser"
                :key="f.signatureKey"
                class="field-row-card p-3 mb-2 rounded border d-flex align-items-center justify-content-between"
                :class="{ 'bg-light': isSigned(f) }"
              >
                <div class="d-flex align-items-center">
                  <span
                    class="badge badge-pill mr-3 circle-badge"
                    :class="isSigned(f) ? 'badge-success' : 'badge-primary'"
                  >
                    {{ f.signingOrder }}
                  </span>

                  <div class="custom-control custom-checkbox mr-3">
                    <input
                      type="checkbox"
                      class="custom-control-input"
                      :id="'chk_' + f.signatureKey"
                      :disabled="isSigned(f) || !isUnlocked(f)"
                      :checked="isSigned(f)"
                      v-model="selectedKeys"
                      :value="f.signatureKey"
                    />
                    <label
                      class="custom-control-label"
                      :for="'chk_' + f.signatureKey"
                    ></label>
                  </div>

                  <div>
                    <div class="font-weight-bold text-dark">
                      {{ f.displayName }}
                    </div>
                    <div class="small text-muted">
                      Signing order {{ f.signingOrder }} ·
                      {{ f.required ? "Required" : "Optional" }}
                    </div>
                    <div v-if="f.reason" class="small text-secondary mt-1">
                      <span class="font-weight-semibold">Reason:</span> {{ f.reason }}
                    </div>
                  </div>
                </div>

                <div>
                  <span class="badge p-2" :class="statusBadgeClass(f)">{{
                    statusText(f)
                  }}</span>
                </div>
              </div>

              <div
                v-if="fieldsForUser.length === 0"
                class="text-center py-4 text-muted"
              >
                No signature fields have been assigned to you for this document.
              </div>
            </template>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="d-flex justify-content-between align-items-center w-100">
          <button
            type="button"
            class="btn btn-secondary btn-sm"
            @click="closePopup"
          >
            Close
          </button>
          <div>
            <button
              type="button"
              class="btn btn-outline-primary btn-sm mr-2"
              :disabled="!hasPendingFields"
              @click="selectAllPending"
            >
              Select all pending fields
            </button>
            <button
              type="button"
              class="btn btn-primary btn-sm"
              :disabled="!selectedKeys.length"
              @click="openSignPopup"
            >
              Sign selected fields
            </button>
          </div>
        </div>
      </template>
    </customModal>

    <!-- Confirm-your-signature popup -->
    <documentSignPopup
      v-if="showSignPopup"
      :fields="chosenFields"
      :email="recipientEmail"
      :saving="savingSignature"
      :server-error="signError"
      @close="closeSignConfirm"
      @confirm="handleConfirmSign"
    />

    <div class="toast-popup" :class="{ show: toastVisible }">
      {{ toastMessage }}
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import axios from "axios";
import store from "../../store/index";
import customModal from "../customModal/customModal.vue";
import documentSignPopup from "./documentSignPopup.vue";
import { jwtDecrypt } from "../../Shared/jwtHelper";

export default {
  name: "DocumentSignaturePopup",
  components: { customModal, documentSignPopup },
  props: {
    envelopeId: { type: String, default: "" },
    recipientEmail: { type: String, default: "" },
    signorder: { type: [String, Number], default: "" },
    documentId: { type: String, required: true },
    version: { type: [String, Number], default: "" },
    fileName: { type: String, default: "" },
    folderId: { type: String, default: "" },
  },
  emits: ["close", "signed"],
  data() {
    return {
      baseurl: process.env.VUE_APP_Service_URL,
      loadingFields: true,
      signatureConfigs: [],
      selectedKeys: [],
      showSignPopup: false,
      chosenFields: [],
      savingSignature: false,
      signError: "",
      toastVisible: false,
      toastMessage: "",
      toastTimer: null,
      closeTimer: null,
    };
  },
  computed: {
    documentTitle() {
      return this.fileName || "Untitled document";
    },
    shortDocId() {
      return this.documentId ? String(this.documentId).slice(0, 8) + "…" : "";
    },
    fieldsForUser() {
      console.log("recipientEmail", this.recipientEmail);
      return (this.signatureConfigs || [])
        .slice()
        .sort((a, b) => a.signingOrder - b.signingOrder);
    },
    lowestUnsignedOrder() {
      const orders = this.signatureConfigs
        .filter((f) => !this.isSigned(f))
        .map((f) => f.signingOrder);
      return orders.length ? Math.min(...orders) : Infinity;
    },
    hasPendingFields() {
      return this.fieldsForUser.some(
        (f) => !this.isSigned(f) && this.isUnlocked(f)
      );
    },
    listHint() {
      const remaining = this.fieldsForUser.filter(
        (f) => !this.isSigned(f)
      ).length;
      return remaining
        ? `${remaining} field${remaining > 1 ? "s" : ""} pending`
        : "All fields signed";
    },
  },
  async mounted() {
    await this.loadSignatureConfigs();
  },
  beforeUnmount() {
    clearTimeout(this.toastTimer);
    clearTimeout(this.closeTimer);
  },
  methods: {
    async loadSignatureConfigs() {
      this.loadingFields = true;
      try {
        const idtoken = store.getters.getIdToken;
        const res = await axios.get(
          `${
            this.baseurl
          }/signature/signatureconfig/getconfigforcurrentuser?documentId=${encodeURIComponent(
            this.documentId
          )}&version=${encodeURIComponent(this.version)}`,
          {
            headers: {
              Authorization: "Bearer " + idtoken,
              "Content-Type": "application/json",
            },
          }
        );

        const records = Array.isArray(res.data)
          ? res.data
          : res.data
          ? [res.data]
          : [];
        const currentVersion = String(this.version || "");
        const matchingRecords = records.filter((record) => {
          if (!record || !record.documentVersion || !currentVersion)
            return true;
          return String(record.documentVersion) === currentVersion;
        });

        this.signatureConfigs = matchingRecords
          .flatMap((record) => {
            const configId =
              record.id || record.configurationId || record.documentId;
            const envId = record.envelopsId || record.envelopeId;
            const signatures = Array.isArray(record.signaturesConfigs)
              ? record.signaturesConfigs
              : Array.isArray(record.signatures)
              ? record.signatures
              : [];
            return signatures.map((sig) => ({
              ...sig,
              configurationId: configId,
              envelopeId: envId,
              signedAt: sig.signedAt || sig.signedDatetime || null,
            }));
          })
          .filter((sig) => sig && sig.isActive !== false);
      } catch (err) {
        console.error("Error fetching signature configuration:", err);
        this.signatureConfigs = [];
      } finally {
        this.loadingFields = false;
      }
    },
    isSigned(f) {
      return !!f.signedAt || f.alreadySigned === true;
    },
    isUnlocked(f) {
      return this.isSigned(f) || f.signingOrder === this.lowestUnsignedOrder;
    },
    statusBadgeClass(f) {
      if (this.isSigned(f)) return "badge-success";
      return this.isUnlocked(f) ? "badge-warning text-dark" : "badge-secondary";
    },
    statusText(f) {
      if (this.isSigned(f)) return "Signed";
      return this.isUnlocked(f)
        ? "Awaiting your signature"
        : "Awaiting an earlier signatory";
    },
    selectAllPending() {
      this.selectedKeys = this.fieldsForUser
        .filter((f) => !this.isSigned(f) && this.isUnlocked(f))
        .map((f) => f.signatureKey);
    },
    openSignPopup() {
      if (!this.selectedKeys.length) return;
      this.chosenFields = this.signatureConfigs.filter((f) =>
        this.selectedKeys.includes(f.signatureKey)
      );

      this.signError = "";
      this.showSignPopup = true;
    },
    closeSignConfirm() {
      if (this.savingSignature) return;
      this.showSignPopup = false;
    },
    async handleConfirmSign({ username, password }) {
      this.savingSignature = true;
      this.signError = "";
      try {
        const idtoken = store.getters.getIdToken;
        let loggedInUserName = "";

        for (const field of this.chosenFields) {
          const payload = {
            envelopeId: this.envelopeId || field.envelopeId || "",
            configurationId: field.configurationId || this.documentId || "",
            signatureKey: field.signatureKey || "",
            location: "India",
            saveProperty: null,
            userName: username || "",
            email: this.recipientEmail,
            password: password || "",
            reason: field.reason || "",
          };

          await axios.post(
            `${this.baseurl}/signature/signature/authorizeandsigndocument`,
            payload,
            {
              headers: {
                Authorization: "Bearer " + idtoken,
                "Content-Type": "application/json",
              },
            }
          );
        }

        const now = new Date().toISOString();
        this.chosenFields.forEach((f) => {
          f.signedAt = now;
        });

        this.showSignPopup = false;
        this.selectedKeys = [];
        this.showToast(
          this.chosenFields.length > 1
            ? `${this.chosenFields.length} signatures applied`
            : "Signature applied"
        );
        alert(this.$t("navigations.document signed successfully"));

        const signedKeys = this.chosenFields.map((f) => f.signatureKey);
        this.closeTimer = setTimeout(() => {
          this.$emit("signed", signedKeys);
        }, 700);
      } catch (err) {
        console.error("Error signing document:", err);
        const responseData = err && err.response && err.response.data;
        this.signError =
          (responseData && (responseData.message || responseData.detail)) ||
          "Unable to apply signature. Please try again.";
      } finally {
        this.savingSignature = false;
      }
    },
    closePopup() {
      this.$emit("close");
    },
    showToast(msg) {
      this.toastMessage = msg;
      this.toastVisible = true;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toastVisible = false;
      }, 2600);
    },
  },
};
</script>

<style scoped>
.circle-badge {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 12px;
}
.field-row-card {
  transition: background-color 0.2s;
}
.toast-popup {
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  background: #1c2430;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  padding: 12px 20px;
  border-radius: 8px;
  box-shadow: 0 18px 48px rgba(20, 28, 40, 0.22);
  display: none;
  align-items: center;
  gap: 8px;
  z-index: 10090;
}
.toast-popup.show {
  display: flex;
}
.btn-primary {
  background-color: #0069d9;
  border-color: #0062cc;
  color: #ffffff !important;
}
.btn-primary:hover,
.btn-primary:focus {
  background-color: #0069d9;
  border-color: #0062cc;
}
.btn-primary:disabled {
  background-color: #a7dfd3 !important;
  border-color: #a7dfd3 !important;
  opacity: 0.7;
}
</style>